import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import projectsData from '../utils/projectsData';
import formatAestheticText from "../utils/textUtils.jsx";
import {
    SubHeadline,
    PageTitle,
} from '../components/ui/PageElements';
import {
    CategoryFilterContainer,
    FilterButton
} from "../components/ui/FilterControls.jsx";
import ResponsiveImage from '../components/ui/ResponsiveImage';
import {media} from "../styles/breakpoints.js";
import { arrowUpRightIcon } from "../components/ui/icons.js";
import PageMeta from '../components/PageMeta';


// Styled components
const ProjectsWrapper = styled.div`
    margin: 50px 0 100px;
    position: relative;
`;

const ProjectsHeader = styled.div`
    margin-bottom: 60px;
    position: relative;
    text-align: center;
`;

const ProjectsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 50px;
`;

const ProjectCardContainer = styled.div`
    position: relative;
    padding-top: 15px; /* Make space for the featured badge */
`;

const ProjectCard = styled(Link)`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.5);
    padding: 40px;
    position: relative;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    display: flex;
    align-items: center;
    gap: 40px;
    text-decoration: none;

    &:hover {
        transform: translateY(-10px);
        box-shadow: 0 10px 30px rgba(255, 51, 153, 0.2);
        border-color: var(--primary-color);
    }

    &:before {
        content: "";
        position: absolute;
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        background-image: url("${arrowUpRightIcon}");
        background-size: contain;
        background-repeat: no-repeat;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 2;
    }

    &:hover:before {
        opacity: 1;
    }

    @media (max-width: 900px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 30px;
    }
`;



const ProjectContent = styled.div`
    flex: 1;
    order: 1;

    @media (max-width: 900px) {
        width: 100%;
        order: 2;
    }
`;

const ProjectCategory = styled.div`
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--primary-color);
    margin-bottom: 10px;
`;

const ProjectTitle = styled.h2`
    font-size: 28px;
    margin-bottom: 15px;
    font-weight: 700;
    color: var(--text-color);
`;

const ProjectDescription = styled.div`
    color: var(--text-muted);
    margin-bottom: 20px;
`;

const ShortDescription = styled.p`
    font-size: 16px;
    margin-bottom: 15px;
    color: #cccccc;
`;

const FullDescription = styled.p`
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 20px;
    white-space: pre-line;
`;

const ProjectMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
`;

const ProjectYear = styled.div`
    font-size: 12px;
    color: #888888;
`;

const ProjectTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const ProjectTag = styled.span`
    font-size: 11px;
    padding: 3px 8px;
    border: 1px solid var(--grid-color);
    position: relative;
    color: var(--text-muted);
    transition: border-color 0.3s ease;
    
    ${ProjectCard}:hover & {
        border-color: var(--primary-color);
    }
`;

const FeaturedBadge = styled.div`
    position: absolute;
    top: 0;
    left: 20px;
    background-color: var(--primary-color);
    color: var(--background-color);
    font-size: 11px;
    padding: 5px 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 700;
    z-index: 1;
`;

const NoResultsMessage = styled.div`
  width: 100%;
  text-align: center;
  padding: 50px;
  border: 1px dashed var(--grid-color);
  font-size: 16px;
  color: var(--primary-color);
`;

const ProjectsPage = () => {
    // Get URL parameters for initial filtering
    const { category } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Extract category from query params if present
    const queryParams = new URLSearchParams(location.search);
    const queryCategory = queryParams.get('category');

    // Set initial active category from URL params or query
    const [activeCategory, setActiveCategory] = useState('ALL');

    // Effect to handle URL parameters for category filtering
    useEffect(() => {
        // Priority: 1. category from query param, 2. category from route param, 3. reset to ALL
        if (queryCategory) {
            setActiveCategory(queryCategory.toUpperCase());
        } else if (category) {
            setActiveCategory(category.toUpperCase());
        } else {
            setActiveCategory('ALL');
        }
        // Scroll to top after DOM update when navigating via URL (e.g., footer links)
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
        });
    }, [queryCategory, category]);

    // Custom sort for categories: OTHER should be last
    const allCategories = [...new Set(projectsData.map(project => project.category.toUpperCase()))];
    const otherCategory = 'OTHER';
    const sortedCategories = allCategories
        .filter(category => category !== otherCategory)
        .sort();
    if (allCategories.includes(otherCategory)) {
        sortedCategories.push(otherCategory);
    }
    const categories = ['ALL', ...sortedCategories];

    // Filter projects by category
    const filtered = activeCategory === 'ALL'
        ? projectsData
        : projectsData.filter(project => project.category.toUpperCase() === activeCategory);

    // Sort projects: featured first, then by priority descending, then by year descending
    const filteredProjects = filtered.slice().sort((a, b) => {
        // Featured projects first
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;

        // Then sort by priority descending (higher priority first, default 0)
        const priorityA = a.priority || 0;
        const priorityB = b.priority || 0;
        if (priorityA !== priorityB) return priorityB - priorityA;

        // Then sort by year descending
        return b.year - a.year;
    });

    // Handle category change
    const handleCategoryChange = (newCategory) => {
        setActiveCategory(newCategory);

        // Update URL query parameter
        if (newCategory === 'ALL') {
            navigate('/projects');
        } else {
            navigate(`/projects?category=${newCategory.toLowerCase()}`);
        }
    };
    const ProjectImage = styled(ResponsiveImage)`
    order: 2;
    flex-shrink: 0;
    width: 300px;
    height: auto; /* Keep natural aspect ratio */
    transition: transform 0.5s ease;

    ${ProjectCard}:hover & {
        transform: scale(1.05);
    }

    @media (max-width: 900px) {
        order: 1;
        margin-bottom: 20px;
        width: auto;
        max-width: 100%;
        height: auto; /* Preserve aspect ratio on mobile */
        max-height: 40vh; /* Prevent overly tall images when stacked above card */
        display: block;
        margin-left: auto;
        margin-right: auto;
    }

    img {
        order: 2;
        flex-shrink: 0;
        width: 300px;
        height: auto;
        transition: transform 0.5s ease;

        @media (max-width: 900px) {
            order: 1;
            margin-bottom: 20px;
            width: auto;
            max-width: 100%;
            height: auto;
            max-height: 40vh;
            display: block;
            margin-left: auto;
            margin-right: auto;
        }
    }

    ${ProjectCard}:hover img {
        transform: scale(1.05);
    }
 `;
    return (
        <ProjectsWrapper>
            <PageMeta
                title="Projects"
                description="Portfolio of audio plugins, games, and creative tools. Featuring RSO synthesizer, A Cat Game, PukaPok, and more."
                canonicalPath="/projects"
            />
            <ProjectsHeader>
                <PageTitle>PROJECTS</PageTitle>
                <SubHeadline>
                    Explore my portfolio of digital creations, ranging from audio software and games to productivity
                    tools and experimental projects.
                </SubHeadline>
            </ProjectsHeader>

            <CategoryFilterContainer>
                {categories.map(category => (
                    <FilterButton
                        key={category}
                        $active={activeCategory === category}
                        onClick={() => handleCategoryChange(category)}
                        aria-pressed={activeCategory === category}
                    >
                        {category}
                    </FilterButton>
                ))}
            </CategoryFilterContainer>

            <ProjectsList>
                {filteredProjects.length > 0 ? (
                    filteredProjects.map(project => (
                        <ProjectCardContainer key={project.id}>
                            {project.featured && <FeaturedBadge>Featured</FeaturedBadge>}
                            <ProjectCard
                                to={`/projects/${project.id}`}
                                aria-label={`View ${project.title} project`}
                            >
                                <ProjectContent>
                                    <ProjectCategory>{project.category}</ProjectCategory>
                                    <ProjectTitle>{formatAestheticText(project.title)}</ProjectTitle>
                                    <ProjectDescription>
                                        <ShortDescription>{formatAestheticText(project.description)}</ShortDescription>
                                        <FullDescription>{formatAestheticText(project.fullDescription || '')}</FullDescription>
                                    </ProjectDescription>
                                    <ProjectMeta>
                                        <ProjectYear>{project.year || 'Ongoing'}</ProjectYear>
                                        <ProjectTags>
                                            {project.tags.map((tag, index) => (
                                                <ProjectTag key={index}>{tag}</ProjectTag>
                                            ))}
                                        </ProjectTags>
                                    </ProjectMeta>
                                </ProjectContent>
                                    <ProjectImage
                                        src={project.imageSrc || '/api/placeholder/400/250'}
                                        alt={project.title}
                                    />
                            </ProjectCard>
                        </ProjectCardContainer>
                    ))
                ) : (
                    <NoResultsMessage>
                        No projects found in this category. Check back later!
                    </NoResultsMessage>
                )}
            </ProjectsList>
        </ProjectsWrapper>
    );
};

export default ProjectsPage;